import type { Context, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import type { GlobalApiInstitutionDetails } from '@data/global-api-institutions';
import {
  GlobalApiInstitutionDetailsSchema,
  INSTITUTIONS_LIST_MOCK,
  useGetGlobalApiInstitutionDetails,
} from '@data/global-api-institutions';
import {
  captureException,
  captureSchemaValidationError,
  getZodSchemaValidationError,
} from '@utility/checkout-utils';

import type {
  GlobalApiInstitutionDetailsContextData,
  GlobalApiInstitutionDetailsContextProviderProps,
} from './globalApiInstitutionDetailsContext.types';
import { findMatchingInstitution } from './globalAPiInstitutionDetailsContext.utils';

const GlobalApiInstitutionDetailsContext =
  createContext<GlobalApiInstitutionDetailsContextData | null>(null);

const useGlobalApiInstitutionDetailsContext = () => {
  const context = useContext(GlobalApiInstitutionDetailsContext);

  if (!context) {
    throw new Error(
      'useGlobalApiInstitutionDetailsContext hook must be used within a <GlobalApiInstitutionDetailsContextProvider>'
    );
  }

  return context;
};

const GlobalApiInstitutionDetailsContextProvider = ({
  children,
  authToken,
  institutionId,
  voltApp,
  voltService,
  institutionsList,
  skip,
}: GlobalApiInstitutionDetailsContextProviderProps) => {
  const [localInstitutionDetails, setLocalInstitutionDetails] =
    useState<GlobalApiInstitutionDetails | null>(
      findMatchingInstitution(institutionsList, institutionId)
    );

  const {
    result: apiInstitutionDetails,
    isLoading,
    error: apiError,
    isError: isApiError,
  } = useGetGlobalApiInstitutionDetails({
    institutionId,
    voltApp,
    voltService,
    authToken: authToken ?? '',
    skip: skip || Boolean(localInstitutionDetails) || !authToken,
  });

  useEffect(() => {
    if (localInstitutionDetails?.id === institutionId) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalInstitutionDetails(
      findMatchingInstitution(institutionsList, institutionId)
    );
  }, [institutionId, institutionsList, localInstitutionDetails]);

  const institutionDetails = localInstitutionDetails ?? apiInstitutionDetails;

  const validationError =
    getZodSchemaValidationError<GlobalApiInstitutionDetails>(
      institutionDetails,
      GlobalApiInstitutionDetailsSchema
    );

  const error = apiError || validationError;
  const isError = isApiError || Boolean(validationError);

  useEffect(() => {
    if (isApiError) {
      captureException(apiError);
    }

    if (validationError) {
      captureSchemaValidationError(validationError, {
        contexts: {
          response: {
            apiResponse: institutionDetails,
          },
          error: {
            schemaError: validationError.issues,
          },
        },
      });
    }
  }, [isApiError, institutionDetails, validationError, apiError]);

  return (
    <GlobalApiInstitutionDetailsContext.Provider
      value={{
        institutionDetails,
        isLoading,
        isError,
        error,
        isApiError,
        apiError,
      }}
    >
      {children}
    </GlobalApiInstitutionDetailsContext.Provider>
  );
};

const withGlobalApiInstitutionDetailsContextProvider =
  (props: Omit<GlobalApiInstitutionDetailsContextProviderProps, 'children'>) =>
  ({ children }: PropsWithChildren) => (
    <GlobalApiInstitutionDetailsContextProvider {...props}>
      {children}
    </GlobalApiInstitutionDetailsContextProvider>
  );

const withGlobalApiInstitutionDetailsContextDecorator = (
  data?: Partial<GlobalApiInstitutionDetailsContextData>
) => ({
  context:
    GlobalApiInstitutionDetailsContext as Context<GlobalApiInstitutionDetailsContextData>,
  contextValue: ({
    args,
  }: {
    args: Partial<GlobalApiInstitutionDetailsContextData>;
  }) => ({
    institutionDetails: INSTITUTIONS_LIST_MOCK[0],
    isLoading: false,
    isApiError: false,
    apiError: null,
    isError: false,
    error: null,
    ...data,
    ...args,
  }),
});

export {
  GlobalApiInstitutionDetailsContext,
  GlobalApiInstitutionDetailsContextProvider,
  useGlobalApiInstitutionDetailsContext,
  withGlobalApiInstitutionDetailsContextProvider,
  withGlobalApiInstitutionDetailsContextDecorator,
};
